import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useRequestOtp, useVerifyOtp } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const emailSchema = z.object({ email: z.string().email() });
const codeSchema = z.object({ code: z.string().regex(/^\d{6}$/, "6 digits") });

export default function LoginPage() {
  const [email, setEmail] = useState<string | null>(null);
  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const navigate = useNavigate();

  const emailForm = useForm<{ email: string }>({ resolver: zodResolver(emailSchema) });
  const codeForm = useForm<{ code: string }>({ resolver: zodResolver(codeSchema) });

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6 rounded-2xl border border-border bg-card/80 p-8 shadow-[0_4px_30px_rgba(0,0,0,0.06)] backdrop-blur">
        <div>
          <div className="text-heading text-2xl font-light">
            <span className="text-bitrefinery-red">Mail</span> Admin
          </div>
          <p className="mt-2 text-sm text-bitrefinery-text text-body">
            {email ? `Enter the code sent to ${email}.` : "Sign in with an allowed admin email."}
          </p>
        </div>

        {!email ? (
          <form
            className="space-y-3"
            onSubmit={emailForm.handleSubmit(async ({ email }) => {
              await requestOtp.mutateAsync(email);
              setEmail(email);
            })}
          >
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" {...emailForm.register("email")} />
              {emailForm.formState.errors.email && (
                <p className="text-xs text-destructive">
                  {emailForm.formState.errors.email.message}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={requestOtp.isPending}>
              {requestOtp.isPending ? "Sending…" : "Send code"}
            </Button>
          </form>
        ) : (
          <form
            className="space-y-3"
            onSubmit={codeForm.handleSubmit(async ({ code }) => {
              try {
                await verifyOtp.mutateAsync({ email, code });
                navigate("/mailboxes", { replace: true });
              } catch {
                codeForm.setError("code", { message: "Invalid or expired code" });
              }
            })}
          >
            <div className="space-y-1.5">
              <Label htmlFor="code">6-digit code</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                {...codeForm.register("code")}
              />
              {codeForm.formState.errors.code && (
                <p className="text-xs text-destructive">
                  {codeForm.formState.errors.code.message}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={verifyOtp.isPending}>
              {verifyOtp.isPending ? "Verifying…" : "Verify"}
            </Button>
            <button
              type="button"
              className="w-full text-xs text-muted-foreground hover:underline"
              onClick={() => {
                setEmail(null);
                codeForm.reset();
              }}
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
